
import React, { useState, useEffect, useMemo } from "react";
import styles from "../../styles/CampusStyles/PantallaDeCampus.module.css";
import calificacionesStyles from "../RegistroCalificacionesScreen/RegistroCalificaciones.module.css";
import Sidebar from "../../components/Sidebar";
import HeaderGlobal from "../../components/HeaderGlobal";
import Pagination from "../../components/Pagination.tsx";
import { getGruposAsesorMe, getInfoAsesorUsuario } from "../../api/calificaciones";
import { getGruposCampus } from "../../api/calificacionesEscolar";
import { useAuth } from "../../api/AuthContext";



function getCiclosUnicos(grupos) {
  const ciclos = Array.from(new Set(grupos.map(g => g.ciclo)));
  // Ordenar en forma descendente: del más reciente al más antiguo
  ciclos.sort((a, b) => {
    // Intenta comparar como números si tienen formato "2026-1", "2025-2", etc.
    const partsA = a.split('-').map(Number);
    const partsB = b.split('-').map(Number);
    const yearDiff = partsB[0] - partsA[0]; // Año descendente
    return yearDiff !== 0 ? yearDiff : (partsB[1] || 0) - (partsA[1] || 0); // Período descendente
  });
  return ciclos;
}


function FirmaConcetradosGruposScreen({ onSeleccionarGrupo }) {
  const { user } = useAuth();
  const [grupos, setGrupos] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [ciclo, setCiclo] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const pageSize = 8;
  const ciclos = getCiclosUnicos(grupos);
  
  // Establecer ciclo por default al más reciente cuando se cargan grupos
  useEffect(() => {
    if (ciclos.length > 0 && !ciclo) {
      setCiclo(ciclos[0]); // El primer ciclo ya está ordenado descendente (más reciente)
    }
  }, [ciclos, ciclo]);
  const gruposFiltrados = useMemo(() => {
    return grupos.filter((grupo) => {
      const texto = `${grupo.codigo} ${grupo.nombre} ${grupo.campus} ${grupo.programa} ${grupo.ciclo}`.toLowerCase();
      const matchBusqueda = texto.includes(busqueda.toLowerCase());
      const matchCiclo = ciclo ? grupo.ciclo === ciclo : true;
      return matchBusqueda && matchCiclo;
    });
  }, [grupos, busqueda, ciclo]);

  const totalPages = Math.max(1, Math.ceil(gruposFiltrados.length / pageSize));
  const gruposPaginados = gruposFiltrados.slice((page - 1) * pageSize, page * pageSize);

  // Cargar grupos del asesor autenticado
  useEffect(() => {
    async function cargarGrupos() {
      setLoading(true);
      setError("");
      try {
        // Verificar si el usuario tiene asesor vinculado (método confiable,
        // independiente del nombre del rol). Si lo tiene → cargar sólo sus grupos
        // vía el endpoint /me/ (no expone id_asesor al cliente). Si no → es
        // personal de Control Escolar y puede ver todos los grupos.
        const info = await getInfoAsesorUsuario();
        if (info?.asesor?.id_asesor) {
          const data = await getGruposAsesorMe();
          const gruposAsesor = data.grupos || [];
          if (gruposAsesor.length === 0) {
            setError("No se encontraron grupos asignados a este asesor.");
          }
          setGrupos(gruposAsesor);
        } else {
          // Sin asesor vinculado → Control Escolar / Administrador
          const data = await getGruposCampus({});
          setGrupos(data.grupos || []);
        }
      } catch (e) {
        setError("Error al cargar grupos");
      } finally {
        setLoading(false);
      }
    }
    cargarGrupos();
  }, [user]);

  // Reinicia paginación al cambiar filtros o dataset base
  useEffect(() => {
    setPage(1);
  }, [busqueda, ciclo, grupos.length]);

  // Ajusta página si queda fuera de rango
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  return (
    <main className={styles.campusScreen}>
      <div className={styles.mainLayout}>
        <aside className={styles.sidebarColumn}>
          <Sidebar />
        </aside>
        <div className={styles.contentColumn}>
          <div className={styles.contentWrapper}>
            <HeaderGlobal title="Rúbrica - Selección de Grupo" />
            <section className={styles.mainContent}>
              <div style={{margin: '0 32px'}}>
                {/* Tarjeta de búsqueda */}
                <div className={calificacionesStyles.busquedaCard} style={{marginBottom: 24}}>
                  <div className={calificacionesStyles.busquedaHeader}>
                    <div className={calificacionesStyles.busquedaIcono}>
                      <span role="img" aria-label="Buscar">🔍</span>
                    </div>
                    <h2 className={calificacionesStyles.busquedaTitulo}>Búsqueda de Grupo</h2>
                  </div>
                  {/* Filtros en una sola línea */}
                  <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', marginBottom: 18 }}>
                    <div style={{ minWidth: 200 }}>
                      <label style={{ fontWeight: 500, fontSize: 14, color: '#333', marginBottom: 6, display: 'block' }}>Ciclo Escolar</label>
                      <select
                        value={ciclo}
                        onChange={e => setCiclo(e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, background: '#fff', cursor: 'pointer' }}
                      >
                        <option value="">Todos los ciclos</option>
                        {ciclos.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ flex: 1, display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                      <div className={calificacionesStyles.searchInputWrapper} style={{ flex: 1 }}>
                        <svg className={calificacionesStyles.searchIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <circle cx="11" cy="11" r="8"/>
                          <path d="m21 21-4.35-4.35"/>
                        </svg>
                        <input
                          type="text"
                          className={calificacionesStyles.searchInput}
                          placeholder="Buscar Grupo..."
                          value={busqueda}
                          onChange={e => setBusqueda(e.target.value)}
                          onKeyPress={e => e.key === 'Enter' && handleBuscar()}
                        />
                      </div>
                      <button
                        className={calificacionesStyles.buscarButton}
                        onClick={handleBuscar}
                      >
                        BUSCAR
                      </button>
                    </div>
                  </div>
                </div>
                {/* Tabla de grupos */}
                <div className={calificacionesStyles.tablaContainer}>
                  <div style={{ maxHeight: '54vh', overflowY: 'auto', overflowX: 'auto' }}>
                    <table className={calificacionesStyles.tabla}>
                      <thead>
                        <tr className={calificacionesStyles.tablaHeader}>
                          <th>Grupo</th>
                          <th>Campus</th>
                          <th>Programa</th>
                          <th>Ciclo Escolar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          if (loading) {
                            return (
                              <tr>
                                <td colSpan={4} style={{ textAlign: 'center', padding: '32px', color: '#888', fontSize: 16 }}>
                                  Cargando grupos...
                                </td>
                              </tr>
                            );
                          }
                          if (error) {
                            return (
                              <tr>
                                <td colSpan={4} style={{ textAlign: 'center', padding: '32px', color: '#c00', fontSize: 16 }}>
                                  {error}
                                </td>
                              </tr>
                            );
                          }
                          if (gruposFiltrados.length === 0) {
                            return (
                              <tr>
                                <td colSpan={4} style={{ textAlign: 'center', padding: '32px', color: '#888', fontSize: 16 }}>
                                  No se encontraron grupos asociados
                                </td>
                              </tr>
                            );
                          }
                          return gruposPaginados.map(grupo => (
                            <tr
                              key={grupo.id_grupo}
                              className={calificacionesStyles.tablaFila}
                              onClick={() => onSeleccionarGrupo(grupo)}
                              style={{ cursor: 'pointer' }}
                            >
                              <td>{grupo.codigo} - {grupo.nombre}</td>
                              <td>{grupo.campus}</td>
                              <td>{grupo.programa}</td>
                              <td>{grupo.ciclo}</td>
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                  {!loading && !error && totalPages > 1 && (
                    <Pagination
                      page={page}
                      totalPages={totalPages}
                      onPageChange={setPage}
                    />
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </main>
  );
  // Handler de búsqueda
  function handleBuscar() {
    // El filtrado ya se hace en el render, pero aquí podrías hacer fetch si fuera necesario
    setBusqueda(busqueda.trim());
  }
}

export default FirmaConcetradosGruposScreen;
